import { useEffect, useState } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const fmtH = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 1 }).format(v));

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{children}</div>
    </div>
  );
}

export default function AssignmentPanel({
  open,
  onClose,
  payload, // { assignment, person, months, year, paymentTerms, vatRates }
  canEdit,
}) {
  const [months, setMonths] = useState({});
  const [saving, setSaving] = useState("");
  const [actualDrafts, setActualDrafts] = useState({});

  const isSub = payload?.person?.engagement_type === "SUBCONTRACTOR";

  useEffect(() => {
    if (open && payload) {
      const init = {};
      for (let m = 1; m <= 12; m++) {
        const c = payload.months[m] ?? payload.months[String(m)] ?? {};
        init[m] = { ...c };
      }
      setMonths(init);
      setActualDrafts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload?.assignment?.id, payload?.year]);

  if (!payload) return null;
  const { assignment, person, year, paymentTerms, vatRates } = payload;

  const put = async (m, fields) => {
    setSaving(`${m}`);
    try {
      await api.put("/assignment-months", {
        assignment_id: assignment.id,
        year,
        month: m,
        ...fields,
      });
      setMonths((prev) => ({ ...prev, [m]: { ...prev[m], ...fields } }));
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setSaving("");
    }
  };

  const applyForward = async (m, field, value) => {
    try {
      const res = await api.post(`/assignments/${assignment.id}/apply-forward`, {
        year,
        month: m,
        [field]: value,
      });
      toast.success(`Applied to ${res.data.updated} month(s)`);
      setMonths((prev) => {
        const next = { ...prev };
        for (let i = m; i <= 12; i++) {
          if (next[i]?.editable) next[i] = { ...next[i], [field]: value };
        }
        return next;
      });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveActual = async (m) => {
    const d = actualDrafts[m] || {};
    try {
      await api.put("/assignment-actuals", {
        assignment_id: assignment.id,
        year,
        month: m,
        billed_hours: Number(d.billed_hours ?? months[m]?.actual?.billed_hours ?? 0),
        actual_revenue:
          d.actual_revenue === "" || d.actual_revenue == null
            ? months[m]?.actual?.actual_revenue ?? null
            : Number(d.actual_revenue),
        source: "MANUAL",
        verified: true,
      });
      toast.success(`${MONTHS[m - 1]} actual saved`);
      setMonths((prev) => ({
        ...prev,
        [m]: {
          ...prev[m],
          actual: {
            billed_hours: Number(d.billed_hours ?? prev[m]?.actual?.billed_hours ?? 0),
            actual_revenue:
              d.actual_revenue === "" || d.actual_revenue == null
                ? prev[m]?.actual?.actual_revenue ?? null
                : Number(d.actual_revenue),
            verified: true,
            source: "MANUAL",
          },
        },
      }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const num = (v) => (v === "" || v == null ? null : Number(v));

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[880px] sm:max-w-[880px] overflow-y-auto" data-testid="assignment-panel">
        <SheetHeader>
          <SheetTitle className="font-heading">{assignment.name}</SheetTitle>
          <SheetDescription>Monthly commercial assumptions for {year}</SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-4 gap-4 mt-5" data-testid="assignment-panel-info">
          <Field label="Consultant">{person.name}</Field>
          <Field label="Customer">{payload.customer_name || "—"}</Field>
          <Field label="Start date">{assignment.start_date || "—"}</Field>
          <Field label="End date">{assignment.end_date || "ongoing"}</Field>
        </div>

        <div className="mt-6 border border-slate-200 rounded-lg overflow-hidden" data-testid="assignment-panel-months">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Month</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Utilization %</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Customer rate</th>
                {isSub && <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Sub cost rate</th>}
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Payment terms</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">VAT</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Billable h</th>
                <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Revenue</th>
                {isSub && <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3">Contribution</th>}
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((label, i) => {
                const m = i + 1;
                const c = months[m] || {};
                const editable = canEdit && c.editable;
                const available = payload.workingHours?.[m] ?? payload.workingHours?.[String(m)];
                const hours = c.closed
                  ? c.actual?.billed_hours ?? 0
                  : available != null && c.forecast_utilization != null
                    ? (available * c.forecast_utilization) / 100
                    : 0;
                const rev = c.closed ? c.actual?.actual_revenue ?? 0 : hours * (c.hourly_sales_rate || 0);
                const subCost = isSub ? hours * (c.subcontractor_hourly_cost_rate || 0) : 0;
                return (
                  <tr key={m} className={`border-b border-slate-100 ${c.closed ? "bg-slate-50" : ""}`} data-testid={`panel-month-row-${m}`}>
                    <td className="py-2 px-3 text-sm font-semibold text-slate-800">
                      {label}
                      {c.closed && <span className="ml-1.5 text-[10px] font-semibold text-slate-500 uppercase">Actual</span>}
                    </td>
                    <td className="py-2 px-3">
                      {editable ? (
                        <Input
                          type="number"
                          min="0"
                          className="h-8 w-20"
                          defaultValue={c.forecast_utilization ?? ""}
                          key={`u-${m}-${c.forecast_utilization}`}
                          onBlur={(e) => {
                            const v = num(e.target.value);
                            if (v !== (c.forecast_utilization ?? null)) put(m, { forecast_utilization: v });
                          }}
                          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                          data-testid={`panel-util-input-${m}`}
                        />
                      ) : (
                        <span className="text-sm text-slate-600">{c.forecast_utilization ?? "—"}</span>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        {editable ? (
                          <>
                            <Input
                              type="number"
                              min="0"
                              className="h-8 w-24"
                              defaultValue={c.hourly_sales_rate ?? ""}
                              key={`r-${m}-${c.hourly_sales_rate}`}
                              onBlur={(e) => {
                                const v = num(e.target.value);
                                if (v !== (c.hourly_sales_rate ?? null)) put(m, { hourly_sales_rate: v });
                              }}
                              onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                              data-testid={`panel-rate-input-${m}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Apply from this month forward"
                              onClick={() => applyForward(m, "hourly_sales_rate", c.hourly_sales_rate)}
                              disabled={c.hourly_sales_rate == null}
                              data-testid={`panel-rate-apply-${m}`}
                            >
                              <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-sm text-slate-600">{fmtSEK(c.hourly_sales_rate)}</span>
                        )}
                      </div>
                    </td>
                    {isSub && (
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          {editable ? (
                            <>
                              <Input
                                type="number"
                                min="0"
                                className="h-8 w-24"
                                defaultValue={c.subcontractor_hourly_cost_rate ?? ""}
                                key={`s-${m}-${c.subcontractor_hourly_cost_rate}`}
                                onBlur={(e) => {
                                  const v = num(e.target.value);
                                  if (v !== (c.subcontractor_hourly_cost_rate ?? null)) put(m, { subcontractor_hourly_cost_rate: v });
                                }}
                                onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                                data-testid={`panel-subrate-input-${m}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Apply from this month forward"
                                onClick={() => applyForward(m, "subcontractor_hourly_cost_rate", c.subcontractor_hourly_cost_rate)}
                                disabled={c.subcontractor_hourly_cost_rate == null}
                                data-testid={`panel-subrate-apply-${m}`}
                              >
                                <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-slate-600">{fmtSEK(c.subcontractor_hourly_cost_rate)}</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        {editable ? (
                          <>
                            <Select
                              value={c.payment_terms_id || ""}
                              onValueChange={(v) => put(m, { payment_terms_id: v })}
                            >
                              <SelectTrigger className="h-8 w-28" data-testid={`panel-terms-select-${m}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                {paymentTerms.map((t) => (
                                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Apply from this month forward"
                              onClick={() => applyForward(m, "payment_terms_id", c.payment_terms_id)}
                              disabled={!c.payment_terms_id}
                              data-testid={`panel-terms-apply-${m}`}
                            >
                              <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-sm text-slate-600">{c.payment_terms_name || "—"}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1">
                        {editable ? (
                          <>
                            <Select
                              value={c.vat_rate_id || ""}
                              onValueChange={(v) => put(m, { vat_rate_id: v })}
                            >
                              <SelectTrigger className="h-8 w-28" data-testid={`panel-vat-select-${m}`}>
                                <SelectValue placeholder="—" />
                              </SelectTrigger>
                              <SelectContent>
                                {vatRates.map((v) => (
                                  <SelectItem key={v.id} value={v.id}>{v.name} ({v.rate}%)</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              title="Apply from this month forward"
                              onClick={() => applyForward(m, "vat_rate_id", c.vat_rate_id)}
                              disabled={!c.vat_rate_id}
                              data-testid={`panel-vat-apply-${m}`}
                            >
                              <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                            </Button>
                          </>
                        ) : (
                          <span className="text-sm text-slate-600">{c.vat_rate_name || "—"}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right text-sm text-slate-700" data-testid={`panel-hours-${m}`}>{fmtH(hours)}</td>
                    <td className="py-2 px-3 text-right text-sm font-semibold text-slate-900" data-testid={`panel-revenue-${m}`}>{fmtSEK(rev)}</td>
                    {isSub && (
                      <td className="py-2 px-3 text-right text-sm text-slate-700" data-testid={`panel-contribution-${m}`}>
                        {rev ? `${fmtSEK(rev - subCost)} (${((rev - subCost) / rev * 100).toFixed(1)}%)` : "—"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {Object.keys(months).some((m) => months[m]?.closed) && (
          <div className="mt-6" data-testid="actuals-section">
            <h3 className="text-sm font-semibold text-slate-900 mb-2">Actuals (closed months)</h3>
            <div className="space-y-2">
              {MONTHS.map((label, i) => {
                const m = i + 1;
                const c = months[m];
                if (!c?.closed) return null;
                const d = actualDrafts[m] || {};
                return (
                  <div key={m} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3" data-testid={`actual-row-${m}`}>
                    <span className="text-sm font-semibold text-slate-800 w-10">{label}</span>
                    {canEdit ? (
                      <>
                        <Input
                          type="number"
                          min="0"
                          className="h-8 w-28"
                          placeholder="Billed hours"
                          value={d.billed_hours ?? c.actual?.billed_hours ?? ""}
                          onChange={(e) => setActualDrafts({ ...actualDrafts, [m]: { ...d, billed_hours: e.target.value } })}
                          data-testid={`actual-hours-input-${m}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          className="h-8 w-32"
                          placeholder="Actual revenue"
                          value={d.actual_revenue ?? c.actual?.actual_revenue ?? ""}
                          onChange={(e) => setActualDrafts({ ...actualDrafts, [m]: { ...d, actual_revenue: e.target.value } })}
                          data-testid={`actual-revenue-input-${m}`}
                        />
                        <Button size="sm" className="h-8 bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => saveActual(m)} data-testid={`actual-save-${m}`}>
                          Save actual
                        </Button>
                      </>
                    ) : (
                      <span className="text-sm text-slate-600">
                        {c.actual ? `${fmtH(c.actual.billed_hours)} h · ${fmtSEK(c.actual.actual_revenue)} SEK` : "No actual recorded"}
                      </span>
                    )}
                    {saving === `${m}` && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="assignment-panel-close">
            <Check className="h-4 w-4 mr-1.5" /> Done
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
